import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from 'primereact/button'

import { NfcTag } from '../../../common/redux/api/web-api'
import { RfidTagDialog } from '../../../common/dialogs/rfid-tag-dialog'
import { WidgetLayout } from '../../../lib/widget-layout'
import { Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableBodyCell } from '../../../lib/table'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'

import { BulkEditRfidTagsDialog } from './dialogs/bulk-edit-rfid-tags-dialog'

const RfidTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

export type AdminUserRfidTagsWidgetProps = {
    nfcTagMessage?: string
    nfcTagError?: string
    nfcTags: NfcTag[]
    onBulkEdit: (nfcTags: NfcTag[]) => Promise<boolean>
    deleteNfcTagFromUser: (nfcTags: NfcTag) => void
    isAdmin: boolean
    style?: React.CSSProperties
}

export const AdminUserRfidTagsWidget = memo(function AdminUserRfidTagsWidget({
    nfcTags,
    isAdmin,
    onBulkEdit,
    deleteNfcTagFromUser,
    style,
}: AdminUserRfidTagsWidgetProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const { id: userId } = useParams()
    const [bulkEditDialog, setBulkEditDialog] = useState({ visible: false, sessionId: 0 })
    const [editRfidTagDialogIsOpen, setEditRfidTagDialogIsOpen] = useState(false)
    const [editTag, setEditTag] = useState<NfcTag>()

    const openBulkEditDialog = () => {
        setBulkEditDialog(({ sessionId }) => ({ visible: true, sessionId: sessionId + 1 }))
    }

    const hideBulkEditDialog = () => {
        setBulkEditDialog((dialog) => ({ ...dialog, visible: false }))
    }

    const hideEditRfidTagDialog = () => {
        setEditRfidTagDialogIsOpen(false)
        setEditTag(undefined)
    }

    const handleBulkEdit = async (tags: NfcTag[]) => {
        const succeeded = await onBulkEdit(tags)

        if (succeeded) hideBulkEditDialog()
    }

    const renderWidgetHeaderButtons = () => {
        return (
            <div className="flex align-items-center gap-2">
                {isAdmin && nfcTags.length ? (
                    <Button
                        type="button"
                        label={text('btnBulkEditRfidTags')}
                        icon="pi pi-pencil"
                        onClick={openBulkEditDialog}
                        data-testid="btn-edit"
                    />
                ) : null}
                {isAdmin ? (
                    <Button
                        type="button"
                        label={text('btnAdd')}
                        icon="pi pi-plus"
                        onClick={() => navigate(`/user/${userId}/userNfcTags`)}
                        data-testid="btn-add"
                    />
                ) : null}
            </div>
        )
    }

    return (
        <div style={style}>
            <WidgetLayout
                title={text('lblRFIDWidget')}
                headerButtons={renderWidgetHeaderButtons()}
                data-testid="rfid-tags"
            >
                <Table data-testid="rfid-tags-table">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell data-testid="lbl-id">
                                <Text code="lblId"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="lbl-comment">
                                <Text code="lblComment"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {nfcTags?.map((nfctag) => (
                            <TableRow key={nfctag.id} data-testid={`row-${nfctag.id}`}>
                                <TableBodyCell>
                                    <RfidTable data-testid={`id-${nfctag.id}`}>
                                        {nfctag.id}
                                        <div>
                                            <Text code="lblExternalId" />:{' '}
                                            {nfctag.externalId ? (
                                                nfctag.externalId
                                            ) : (
                                                <Text code="msgValueNotSet"></Text>
                                            )}
                                        </div>
                                    </RfidTable>
                                </TableBodyCell>
                                <TableBodyCell data-testid={`commet-${nfctag.id}`}>{nfctag.comment}</TableBodyCell>
                                <TableBodyCell className="vertical-align-middle">
                                    {isAdmin && (
                                        <div className="flex justify-content-end gap-2">
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    setEditTag(nfctag)
                                                    setEditRfidTagDialogIsOpen(true)
                                                }}
                                                icon="pi pi-pencil"
                                                aria-label={text('btnEdit')}
                                                tooltip={text('btnEdit')}
                                                size="small"
                                                severity="info"
                                                outlined
                                                rounded
                                                data-testid="btn-edit"
                                            />
                                            <Button
                                                type="button"
                                                onClick={() => deleteNfcTagFromUser(nfctag)}
                                                icon="pi pi-trash"
                                                aria-label={text('btnRemove')}
                                                tooltip={text('btnRemove')}
                                                size="small"
                                                severity="danger"
                                                outlined
                                                rounded
                                                data-testid="btn-delete"
                                            />
                                        </div>
                                    )}
                                </TableBodyCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </WidgetLayout>
            <BulkEditRfidTagsDialog
                key={bulkEditDialog.sessionId}
                visible={bulkEditDialog.visible}
                nfcTags={nfcTags}
                onHide={hideBulkEditDialog}
                onSubmit={handleBulkEdit}
            />
            <RfidTagDialog rfidTag={editTag} visible={editRfidTagDialogIsOpen} onHide={hideEditRfidTagDialog} />
        </div>
    )
})
